import {
  DefectCatalogItem,
  DefectVisualizationRule,
} from '../contracts/availableLayers';
import { DefectFeaturesResponseBody } from '../contracts/defectFeatures';
import {
  DefectRulesetsResponseBody,
  DefectRulesResponseBody,
  toDefectRulesetSlug,
} from '../contracts/defectRulesets';
import type { GeoJsonGeometry } from '../geojsonBase';
import {
  buildRulesFromColorScheme,
  DEFECT_BAND_SLUGS,
} from '../map/buildRulesFromColorScheme';
import { buildLegendSectionLineString } from '../map/legendSurveySectionGeometry';
import {
  filterRulesToWorstCondition,
  isWorstConditionRuleset,
} from '../visualization/worstConditionOnly';
import {
  MOCK_SCANNER_DEFECTS,
  MOCK_SCANNER_GEOMETRY_KIND,
} from './scannerCatalog.mock';

export function isScannerMockCatalogueEnabled(): boolean {
  if (process.env.REACT_APP_SDM_USE_SCANNER_MOCKS === 'false') {
    return false;
  }
  if (process.env.REACT_APP_SDM_USE_SCANNER_MOCKS === 'true') {
    return true;
  }
  return process.env.NODE_ENV === 'development';
}

export function findMockScannerDefect(slug: string): DefectCatalogItem | null {
  return MOCK_SCANNER_DEFECTS.find((defect) => String(defect.slug) === slug) ?? null;
}

export function isMockScannerDefectSlug(slug: string): boolean {
  return findMockScannerDefect(slug) !== null;
}

function findMockMeasurement(
  defect: DefectCatalogItem,
  measurementSlug: string,
): DefectVisualizationRule | null {
  const normalized = measurementSlug.trim().toLowerCase();
  return (
    defect.measurements.find(
      (measurement) =>
        String(measurement.slug).toLowerCase() === normalized ||
        measurement.name.trim().toLowerCase() === normalized,
    ) ?? null
  );
}

export function buildMockScannerRulesets(): DefectRulesetsResponseBody {
  return {
    rulesets: [
      {
        id: 'mock-continuous-score',
        name: 'Continuous',
        slug: toDefectRulesetSlug('continuous-score'),
        default: true,
      },
      {
        id: 'mock-percentile-5',
        name: 'Percentile — 5 bands',
        slug: toDefectRulesetSlug('percentile-5'),
        default: false,
      },
      {
        id: 'mock-percentile-3',
        name: 'Percentile — 3 bands',
        slug: toDefectRulesetSlug('percentile-3'),
        default: false,
      },
      {
        id: 'mock-worst-condition',
        name: 'Percentile — 1 band (worst condition)',
        slug: toDefectRulesetSlug('worst-condition'),
        default: false,
      },
      {
        id: 'mock-custom-bands',
        name: 'Client standard',
        slug: toDefectRulesetSlug('custom-bands'),
        default: false,
      },
    ],
  };
}

export function buildMockScannerRules(
  defectSlug: string,
  measurementSlug?: string,
  rulesetSlug?: string,
): DefectRulesResponseBody | null {
  const defect = findMockScannerDefect(defectSlug);
  if (!defect) {
    return null;
  }

  const measurement =
    (measurementSlug ? findMockMeasurement(defect, measurementSlug) : null) ??
    defect.measurements[0] ??
    null;
  if (!measurement) {
    return { rules: [] };
  }

  const rules = buildRulesFromColorScheme(measurement.colorScheme);
  return {
    rules: isWorstConditionRuleset(rulesetSlug)
      ? filterRulesToWorstCondition(rules)
      : rules,
  };
}


export function buildMockScannerFeatures(
  defectSlug: string,
  measurementSlug: string,
  surveyYearSlug: string,
  lane?: string,
): DefectFeaturesResponseBody | null {
  const defect = findMockScannerDefect(defectSlug);
  if (!defect) {
    return null;
  }

  const measurement =
    findMockMeasurement(defect, measurementSlug) ?? defect.measurements[0] ?? null;
  if (!measurement) {
    return { type: 'FeatureCollection', features: [] };
  }

  const resolvedLane = lane?.trim() || measurement.surveyYears[0]?.lanes[0] || 'left';
  const measurementKey = String(measurement.slug);

  return {
    type: 'FeatureCollection',
    features: DEFECT_BAND_SLUGS.map((band, index) => {
      const threshold = measurement.thresholds[index] ?? index + 1;
      const geometry: GeoJsonGeometry = buildLegendSectionLineString(index);

      return {
        type: 'Feature' as const,
        id: `${defectSlug}-${measurementKey}-${band}`,
        geometry,
        properties: {
          shapeId: `mock-${band}`,
          surveyType: defect.surveyType,
          surveyYear: measurement.surveyYears[0]?.surveyYear ?? surveyYearSlug,
          surveyYearSlug,
          lane: resolvedLane,
          measurement: measurementKey,
          value: threshold,
          band,
          section: `MOCK-${index + 1}`,
          chainage: index * 10,
          geometryKind: MOCK_SCANNER_GEOMETRY_KIND,
        },
      };
    }),
  };
}
